import { useEffect, useRef, useState } from "react";
import { Send, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const ACTIONS = {
  create: { label: "Create in Ramp", cls: "ss-pill-sky" },
  add_contact: { label: "Add contact", cls: "ss-pill-amber" },
  request_details: { label: "Ask for payment details", cls: "ss-pill-amber" },
  ready: { label: "Ready for Bill Pay", cls: "ss-pill-green" },
  skip_no_email: { label: "Skipped — no email in QuickBooks", cls: "ss-pill-gray" },
  skip_bad_email: { label: "Skipped — QuickBooks email isn't valid", cls: "ss-pill-amber" },
};
const ORDER = ["create", "add_contact", "request_details", "skip_no_email", "skip_bad_email", "ready"];
const POLL_MS = 3000;

// 2026-10-22 — sends the company's QuickBooks vendors to Ramp so Ramp Bill
// Pay can pay them. Preview first (nothing changes), then it runs in the
// background. Bank details never come from QuickBooks: Ramp emails each
// vendor to enter their own payment details.
export default function RampVendorPush() {
  const [open, setOpen] = useState(false);
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState(null);
  const [askDetails, setAskDetails] = useState(true);
  const [search, setSearch] = useState("");
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const timer = useRef(null);

  useEffect(() => () => clearTimeout(timer.current), []);

  // The push runs on the server, so it carries on after the window is closed
  // or the page is left; coming back (or reopening the dialog) picks it up again.
  useEffect(() => {
    api.get("/contractor/integrations/ramp/vendor-push/status").then(({ data }) => {
      if (data.running) { setRunning(true); poll(); }
    }).catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const load = async () => {
    setPreview(null); setError(null); setResult(null);
    try {
      const { data } = await api.get("/contractor/integrations/ramp/vendor-push/preview");
      setPreview(data);
    } catch (e) {
      setError(e.response?.data?.detail || "Couldn't read your vendors from Ramp");
    }
  };

  const openDialog = () => { setOpen(true); if (!running) load(); };

  const onOpenChange = (v) => {
    setOpen(v);
    if (!v && running) toast.info("Still sending in the background — you'll get an alert when it's done.");
  };

  const poll = () => {
    timer.current = setTimeout(async () => {
      try {
        const { data } = await api.get("/contractor/integrations/ramp/vendor-push/status");
        if (data.running) return poll();
        setRunning(false);
        setResult(data.result);
        if (data.status === "failed") toast.error(`Ramp: ${data.result?.error || "sending vendors failed"}`);
        else toast.success(`Sent to Ramp — ${data.result.created} created, ${data.result.contacts_added} contacts added`);
      } catch {
        poll();
      }
    }, POLL_MS);
  };

  const send = async () => {
    setRunning(true);
    try {
      await api.post("/contractor/integrations/ramp/vendor-push", { request_payment_details: askDetails });
      poll();
    } catch (e) {
      setRunning(false);
      toast.error(e.response?.data?.detail || "Couldn't start sending vendors");
    }
  };

  const counts = preview?.counts || {};
  const toSend = (counts.create || 0) + (counts.add_contact || 0) + (askDetails ? counts.request_details || 0 : 0);
  const rows = (preview?.vendors || [])
    .filter((v) => !search || v.name.toLowerCase().includes(search.toLowerCase()))
    .sort((a, b) => ORDER.indexOf(a.action) - ORDER.indexOf(b.action) || a.name.localeCompare(b.name));

  return (
    <>
      <button onClick={openDialog} data-testid="ramp-vendor-push-button" className="mt-1.5 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-slate-200 text-[12px] font-semibold hover:bg-slate-50">
        <Send size={12} /> Send QuickBooks vendors to Ramp
      </button>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-2xl" data-testid="ramp-vendor-push-modal">
          <DialogHeader><DialogTitle>Send QuickBooks vendors to Ramp</DialogTitle></DialogHeader>
          <p className="text-[12.5px] text-slate-500 -mt-1">
            Ramp pays a bill only when the vendor has a contact and a payment method. This creates your QuickBooks vendors in Ramp
            with their QuickBooks email as the contact. Bank details never come from QuickBooks — Ramp can email each vendor to enter their own.
          </p>

          {running && (
            <div className="text-[12.5px] text-slate-600 bg-slate-50 border border-slate-100 rounded-lg px-3 py-4 text-center" data-testid="ramp-vendor-push-running">
              <Loader2 size={14} className="inline animate-spin mr-1.5" /> Sending your vendors to Ramp…
              <div className="text-[11.5px] text-slate-400 mt-1">You can close this window. It keeps going, and you'll get an alert when it's done.</div>
            </div>
          )}

          {!running && error && <div className="text-[12.5px] text-red-700 bg-red-50 border border-red-100 rounded-lg px-3 py-2" data-testid="ramp-vendor-push-error">{error}</div>}
          {!running && !preview && !error && <div className="text-[12.5px] text-slate-400 py-6 text-center"><Loader2 size={14} className="inline animate-spin mr-1" /> Comparing QuickBooks and Ramp…</div>}

          {preview && !result && !running && (
            <>
              <div className="flex flex-wrap gap-1.5 text-[11.5px]">
                {ORDER.filter((a) => counts[a]).map((a) => <span key={a} className={`ss-pill ${ACTIONS[a].cls}`}>{counts[a]} · {ACTIONS[a].label}</span>)}
              </div>
              <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search vendors…" className="w-full h-9 px-2.5 rounded-lg border border-slate-200 text-[13px]" />
              <div className="max-h-72 overflow-y-auto ss-scrollbar border border-slate-100 rounded-lg divide-y divide-slate-50">
                {rows.map((v) => (
                  <div key={v.qbo_id} className="flex items-center justify-between gap-3 px-3 py-2 text-[12.5px]" data-testid={`ramp-push-row-${v.qbo_id}`}>
                    <div className="min-w-0">
                      <div className="font-medium truncate">{v.name}</div>
                      <div className="text-[11.5px] text-slate-400 truncate">{v.email || "no email"}</div>
                    </div>
                    <span className={`ss-pill shrink-0 ${ACTIONS[v.action].cls}`}>{ACTIONS[v.action].label}</span>
                  </div>
                ))}
              </div>
              <label className="flex items-start gap-2 text-[12.5px] text-slate-700">
                <input type="checkbox" checked={askDetails} onChange={(e) => setAskDetails(e.target.checked)} className="mt-0.5" data-testid="ramp-ask-details-checkbox" />
                <span>Have Ramp email each vendor to enter their payment details (bank account or check address). <span className="text-amber-700">These emails go to your real vendors.</span></span>
              </label>
              {!preview.can_write && (
                <div className="text-[12px] text-amber-800 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2">
                  Your Ramp app needs the <b>vendors:write</b> scope to send vendors. Add it to the app in Ramp, then disconnect and connect Ramp again here.
                </div>
              )}
              <button disabled={running || !preview.can_write || toSend === 0} onClick={send} data-testid="ramp-vendor-push-send" className="w-full h-10 rounded-lg bg-slate-900 text-white text-[13px] font-semibold disabled:opacity-50">
                {running ? "Sending to Ramp…" : toSend ? `Send ${toSend} vendor${toSend === 1 ? "" : "s"} to Ramp` : "Nothing to send"}
              </button>
            </>
          )}

          {result && (
            <div className="space-y-2 text-[12.5px]" data-testid="ramp-vendor-push-result">
              <div>Created in Ramp: <b>{result.created}</b> · Contacts added: <b>{result.contacts_added}</b> · Asked for payment details: <b>{result.details_requested}</b></div>
              <div className="text-slate-500">Already ready: {result.ready} · Skipped (no email in QuickBooks): {result.skipped_no_email}{result.skipped_bad_email > 0 && ` · Skipped (email isn't valid in QuickBooks): ${result.skipped_bad_email}`}</div>
              {result.failed?.length > 0 && (
                <div className="text-red-700 bg-red-50 border border-red-100 rounded-lg px-3 py-2">
                  {result.failed.length} failed: {result.failed.map((f) => `${f.name} (${f.error})`).join("; ")}
                </div>
              )}
              <button onClick={() => setOpen(false)} className="w-full h-10 rounded-lg border border-slate-200 text-[13px] font-semibold">Done</button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
